import React, {useEffect, useState} from "react";
import {Tab, TabBar, TabPanel, TabsProvider} from "../../components";
import {PartnerDashboardProfile} from "../models/ProfileResponse";
import {clientManagementApiClient} from "../ClientManagementApiClient";
import {setHeaderTickerTabs} from "../ClientProfile/HeaderTickerTabsSlice";
import {EMPTY_HEADER_TICKER_TABS_RESPONSE} from "../ClientProfile/HeaderTickerTabType";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import usePageViewTimer from "../../hooks/usePageViewTimer";
import PartnerDashboard from "./PartnerDashboard";
import {useHistory, useLocation, useParams} from "react-router-dom";
import {RouteWithProfileType} from "../../routes/types";
import {ClientType} from "../constants";
import ResetScreenModal from "../../components/ResetScreenModal";
import {Button, Modal} from "xps-react";
import {monitoringApiClient} from "../../Monitoring/MonitoringApiClient";
import {ConditionResultResponse} from "../../Monitoring/ConditionResultResponse";
import {InvestmentProgramRestoreDto} from "../../Assets/models/InvestmentProgram";
import {setHasInvalidIp, setIpStatusInfo} from "../../Assets/common/ipStatusSlice";
import {selectReleaseToggles} from "../../ReleaseToggles/releaseTogglesSlice";
import {initialProfileInfoState, setRestoredProfileInfoState} from "./ProfileIPInfoSlice";

const getNonNationalSampleProfileIds = (profiles: PartnerDashboardProfile[]) => {
    return profiles.filter((profile) => !profile.isNationalSample).map((profile) => profile.id);
};

const DashboardParent = () => {
    const dispatch = useAppDispatch();

    const [clientProfiles, setClientProfiles] = useState([] as PartnerDashboardProfile[])
    const [profileConditions, setProfileConditions] = useState([] as ConditionResultResponse[]);
    const [isLoading, setIsLoading] = useState(true)
    const {type} = useParams<RouteWithProfileType>();
    const history = useHistory();
    const location = useLocation();
    usePageViewTimer('Partner Dashboard Page Load Timer (milliseconds)', isLoading);
    const [showUnlinkedModal, setShowUnlinkedModal] = useState<boolean>(false);
    const releaseToggles = useAppSelector(selectReleaseToggles);
    const enableNationalSamples = releaseToggles?.enableStandardizedClientPersonas ?? false;

    const getSelectedTabType = () => {
        if (type?.toLowerCase() === ClientType.PROSPECT) {
            return ClientType.PROSPECT;
        } else if (type?.toLowerCase() === ClientType.SAMPLE) {
            return ClientType.SAMPLE;
        } else {
            return ClientType.CLIENT;
        }
    }

    const [activeTabType, setActiveTabType] = useState<ClientType>(getSelectedTabType());

    const handleDashboardTabNavigation = (nextTabType: ClientType, route: string) => {
        if (nextTabType !== activeTabType) {
            dispatch(setRestoredProfileInfoState(initialProfileInfoState.investmentProgramRestoreInfo));
        }
        setActiveTabType(nextTabType);
        history.replace(route);
    };

    function handleUnlinkedModal() {
        setShowUnlinkedModal(!showUnlinkedModal)
    }

    const handleRestoreArchivedProfile = (investmentProgramRestoreDto: InvestmentProgramRestoreDto) => {
        clientManagementApiClient
            .getPartnerDashboardProfiles()
            .then((profiles) => {
                setClientProfiles(profiles.data);
                monitoringApiClient.getConditions(getNonNationalSampleProfileIds(profiles.data))
                    .then((conditionResult) => {
                        setIsLoading(false);
                        setProfileConditions(conditionResult);
                    })
                    .catch((error) => {
                        console.error('Failed to retrieve conditions', error.message);
                    });
            }).catch(error => console.error('Could not fetch partner dashboard profiles', error.message))
    }

    const refreshProfiles = () => {
        clientManagementApiClient
            .getPartnerDashboardProfiles()
            .then((profiles) => {
                setClientProfiles(profiles.data);
                monitoringApiClient.getConditions(getNonNationalSampleProfileIds(profiles.data))
                    .then((conditionResult) => {
                        setProfileConditions(conditionResult);
                    })
                    .catch((error) => {
                        console.error('Failed to retrieve conditions', error.message);
                    });
            }).catch(error => console.error('Could not fetch partner dashboard profiles', error.message));
    };

    useEffect(() => {
        dispatch(setHeaderTickerTabs(EMPTY_HEADER_TICKER_TABS_RESPONSE));
        clientManagementApiClient
            .getPartnerDashboardProfiles()
            .then((profiles) => {
                dispatch(setHasInvalidIp(false));
                dispatch(setIpStatusInfo(null));
                setClientProfiles(profiles.data);
                monitoringApiClient.getConditions(getNonNationalSampleProfileIds(profiles.data))
                    .then((conditionResult) => {
                        setIsLoading(false);
                        setProfileConditions(conditionResult);
                    })
                    .catch((error) => {
                        console.error('Failed to retrieve conditions', error.message);
                    });
            }).catch(error => console.error('Could not fetch partner dashboard profiles', error.message))
    }, [])

    useEffect(() => {
        setActiveTabType(getSelectedTabType());
    }, [type]);

    const getLastSelectedTab = () => {
        if (activeTabType === ClientType.PROSPECT) {
            return 1;
        } else if (activeTabType === ClientType.SAMPLE) {
            return 2;
        } else {
            return 0;
        }
    }

    const sampleProfiles = clientProfiles.filter(profile => profile.sample);
    const nationalSampleProfiles = sampleProfiles.filter(profile => profile.isNationalSample);
    const partnerSampleProfiles = sampleProfiles.filter(profile => !profile.isNationalSample);

    return <div className="display-flex margin-xl">
        <section className="partner-dashboard-section">
            <div className="partner-dashboard-page">
                <Modal
                    header="No Linked Client Partner Workstation (PW) Profile"
                    label="modal-label"
                    id={'no-linked-client-pw'}
                    size='small'
                    isOpen={showUnlinkedModal}
                    showCloseButton={false}
                    showFooter={true}
                    onRequestClose={() => setShowUnlinkedModal(false)}
                    footer={
                        <Button
                            kind={'primary'}
                            onClick={() => setShowUnlinkedModal(false)}
                            size="medium">
                            Okay
                        </Button>
                    }
                ><span className={"no-linked-client-modal"}>To provide access to the Service Team, a PW Client Profile must be linked via Profile Settings.</span>
                </Modal>
                <TabsProvider tabIdArray={["clientsTab", "prospectsTab", "samplesTab"]}
                              defaultActiveIndex={getLastSelectedTab()}>
                    <TabBar size="medium" theme="dark">
                        <Tab className="clients-tab" name="Clients" onClick={() => {
                            handleDashboardTabNavigation(ClientType.CLIENT, '/Dashboard/Client')
                        }}/>
                        <Tab name="Prospects" onClick={() => {
                            handleDashboardTabNavigation(ClientType.PROSPECT, '/Dashboard/Prospect')
                        }}/>
                        <Tab name="Samples" onClick={() => {
                            handleDashboardTabNavigation(ClientType.SAMPLE, '/Dashboard/Sample')
                        }}/>
                    </TabBar>
                    <TabPanel>
                        <PartnerDashboard
                            profiles={clientProfiles.filter(profile => !profile.prospect && !profile.sample)}
                            profileConditions={profileConditions}
                            isLoading={isLoading}
                            tabType={ClientType.CLIENT}
                            handleUnlinkedModal={handleUnlinkedModal}
                            handleRestoreArchivedProfile={handleRestoreArchivedProfile}
                        />
                        <PartnerDashboard profiles={clientProfiles.filter(profile => profile.prospect)}
                                          profileConditions={profileConditions}
                                          isLoading={isLoading}
                                          tabType={ClientType.PROSPECT}
                                          handleUnlinkedModal={handleUnlinkedModal}
                                          handleRestoreArchivedProfile={handleRestoreArchivedProfile}
                        />
                        <PartnerDashboard
                                profiles={partnerSampleProfiles}
                                profileConditions={profileConditions}
                                isLoading={isLoading}
                                tabType={ClientType.SAMPLE}
                                handleUnlinkedModal={handleUnlinkedModal}
                                handleRestoreArchivedProfile={handleRestoreArchivedProfile}
                                enableNationalSamples={enableNationalSamples}
                                nationalProfiles={nationalSampleProfiles}
                                onRefreshProfiles={refreshProfiles}
                        />
                    </TabPanel>
                </TabsProvider>
            </div>
        </section>
        {location.pathname === '/' && <ResetScreenModal/>}
    </div>
}

export default DashboardParent;